import React, { useContext,useCallback, useMemo } from 'react';

// DEPENDENCIES
import isUndefined from 'lodash/isUndefined';
import lowerCase from 'lodash/lowerCase';
import { useFormikContext } from 'formik';

// GLOBAL HELPERS/CONTEXT
import useRecordView from 'helpers/getRecordView';
import { RecordContext } from 'helpers/getRecordData';

// FUNCTIONS
import { checkRole } from 'functions.js';

// GLOBAL COMPONENTS
import Form from 'core/form/Form';
import Card from 'parts/card/Card';

// GLOBAL VARIABLES
import { NEW_USED } from 'codes';

const OWE_ITEM_CONSTRUCTOR = {
  owe_item_id: '',
  item_price: '',
  item_name: '',
  item_add_as_task: false
}
const OTHER_ITEM_CONSTRUCTOR = {
  sales_other_id: '',
  other_item_cost: '',
  other_item: '',
  item_add_as_task: false
}
const BANK_FEE_CONSTRUCTOR = {
  sales_bank_fee_id: '',
  item_amount: '',
  item_name: '',
  item_add_as_task: false
}

// RENDER
const VehicleCostForm = () => {

  // CONTEXT
  const { record: { inventory = {}, ...record } = {} } = useContext(RecordContext);
  const { sales_status_name } = record || {};
  const { setFieldValue } = useFormikContext() || {};
  const { isAdd } = useRecordView();

  const isDelivered = useMemo(() => lowerCase(sales_status_name) === 'delivered', [sales_status_name]);
  const isAdminOrSalesManager = checkRole(['dslAdmin', 'salesManager', 'salesManagerPlus']);

  // MEMOS
  const {
    new_used,
    ...vehicleInfo
  } = useMemo(
    () => isAdd ? record : inventory,
    [record, inventory, isAdd]
  )
  const type = useMemo(
    () => (!isUndefined(new_used) ? (parseInt(new_used) === NEW_USED.new.numeric ? 'Factory Invoice Cost' : 'ACV') : ''),
    [new_used]
  )

  const handleOweItemChange  = useCallback((item,i) => {
    if(!isAdd && item.owe_item_id !== '' ) {
      setFieldValue(`aryOweItem[${i}].isUpdated`,true)
    }
   },[setFieldValue, isAdd])

   const handleOweItemDelete = useCallback((item,i) => {
    if(!isAdd && item.owe_item_id !== '' ) {
      setFieldValue(`aryOweItem[${i}].isUpdated`, false);
      setFieldValue(`aryOweItem[${i}].isDeleted`,true)
      return true;
    }
    else return false;

   },[setFieldValue, isAdd])

   const handleOtherItemChange  = useCallback((item,i) => {
    if(!isAdd && item.sales_other_id !== '' ) {
      setFieldValue(`aryOtherItem[${i}].isUpdated`,true)
    }
   },[setFieldValue, isAdd])

   const handleOtherItemDelete = useCallback((item,i) => {
    if(!isAdd && item.sales_other_id !== '' ) {
      setFieldValue(`aryOtherItem[${i}].isUpdated`, false);
      setFieldValue(`aryOtherItem[${i}].isDeleted`,true)
      return true;
    }
    else return false;

   },[setFieldValue, isAdd])

   const handleBankFeeChange  = useCallback((item,i) => {
    if(!isAdd && item.sales_bank_fee_id !== '' ) {
      setFieldValue(`aryBankFee[${i}].isUpdated`,true)
    }
   },[setFieldValue,isAdd])

   const handleBankFeeDelete = useCallback((item,i) => {
    if(!isAdd && item.sales_bank_fee_id !== '' ) {
      setFieldValue(`aryBankFee[${i}].isUpdated`, false);
      setFieldValue(`aryBankFee[${i}].isDeleted`,true)
      return true;
    }
    else return false;

   },[setFieldValue, isAdd])

  // RENDER
  return (
    <Form.Body>
      <Form.PlainText
        label={type}
        value={vehicleInfo.invoice_cost}
        schema="dollars"
      />
      <Form.PlainText
        label={`${type} with Inventory Pack Total`}
        value={vehicleInfo.invoice_cost_with_inventory_pack}
        schema="dollars"
      />
      <Form.PlainText
        label={`${type} with Repair Invoices`}
        value={vehicleInfo.invoice_cost_with_repair_invoice}
        schema="dollars"
      />
      <Form.PlainText
        label="DMS Cost"
        value={vehicleInfo.dms_invoice_price}
        schema="dollars"
      />
      <Form.Control
        name="cost"
        label="Cost"
        schema="dollars"
        plaintext={isDelivered && !isAdminOrSalesManager}
        required
      />
      <Card.Divider />
      <Form.Table
        name="aryOweItem"
        title="We Owe"
        itemLabel="Owe Item"
        itemConstructor={OWE_ITEM_CONSTRUCTOR}
        handleDelete={(item,i) => handleOweItemDelete(item,i)}
        buttonBefore
      >{(item = {},i) => [
        <Form.Control
          name="item_price"
          label="Amount"
          schema="dollars"
          colWidth={6}
          onChange={() => handleOweItemChange(item,i)}
          required
        />,
        <Form.Control
          name="item_name"
          label="Description"
          onChange={() => handleOweItemChange(item,i)}
          maxLength={100}
          colWidth={12}
          required
        />,
        <Form.YesNo
          name="item_add_as_task"
          text="Add as Task"
          type="checkbox"
          onChange={() => handleOweItemChange(item,i)}
          numeric={false}
          colWidth={6}
        />
      ]}</Form.Table>
      <Card.Divider />
      <Form.Table
        name="aryOtherItem"
        title="Other Items"
        itemLabel="Other Item"
        itemConstructor={OTHER_ITEM_CONSTRUCTOR}
        handleDelete={(item,i) => handleOtherItemDelete(item,i)}
        buttonBefore
      >{(item = {},i) => [
        <Form.Control
          name="other_item_cost"
          label="Amount"
          onChange={() => handleOtherItemChange(item,i)}
          schema="dollars"
          colWidth={6}
          required
        />,
        <Form.Control
          name="other_item"
          label="Description"
          onChange={() => handleOtherItemChange(item,i)}
          maxLength={100}
          colWidth={12}
          required
        />,
        <Form.YesNo
          name="item_add_as_task"
          text="Add as Task"
          type="checkbox"
          onChange={() => handleOtherItemChange(item,i)}
          numeric={false}
          colWidth={6}
        />
      ]}</Form.Table>
      <Card.Divider />
      <Form.Table
        name="aryBankFee"
        title="Bank Fees"
        itemLabel="Bank Fee"
        itemConstructor={BANK_FEE_CONSTRUCTOR}
        handleDelete={(item,i) => handleBankFeeDelete(item,i)}
        buttonBefore
      >{(item = {},i) => [
        <Form.Control
          name="item_amount"
          onChange={() => handleBankFeeChange(item,i)}
          label="Amount"
          schema="dollars"
          colWidth={6}
          required
        />,
        <Form.Control
          name="item_name"
          label="Description"
          maxLength={100}
          onChange={() => handleBankFeeChange(item,i)}
          colWidth={12}
          required
        />,
        <Form.YesNo
          name="item_add_as_task"
          text="Add as Task"
          type="checkbox"
          onChange={() => handleBankFeeChange(item,i)}
          numeric={false}
          colWidth={6}
        />,
      ]}</Form.Table>
    </Form.Body>
  )
}

export default function VehicleCost(props) {
  return <Card title="Vehicle Cost" body={<VehicleCostForm {...props} />} />;
}
